/**
 * 프릭스 계약서 미리보기(iframe)를 컨테이너 폭에 맞춰 크고 선명하게 보여준다.
 *
 * 배경 — 2026-08-24 운영 화면 실측:
 * 1. 프릭스 뷰어(app.prix.im)는 cross-origin iframe 이라 내부 줌을 직접 제어할 수 없고,
 *    preview URL 에도 배율 파라미터가 없다(파라미터는 mode / object-* 뿐).
 * 2. 뷰어는 **로드 시점의 iframe 폭**을 보고 배율을 이산적으로 고른다.
 *    폭 673px·850px -> 문서 350px / 폭 1100px -> 문서 약 702px(100%).
 *    로드 뒤에 폭만 바꾸면 재계산하지 않는다.
 * 3. 그래서 iframe 을 항상 IFRAME_WIDTH(1100px)로 두어 100% 배율을 얻는다.
 *    이때 문서는 iframe 폭의 약 DOC_RATIO(63.8%)를 차지하며 iframe 안에서 가운데 놓인다.
 *
 * 문제 — 100% 배율 문서(702px)는 미리보기 컨테이너(약 673px)보다 **크기 때문에 그냥 두면 양옆이 잘린다.**
 * 컨테이너 폭은 화면 크기에 따라 연속적으로 변하는데 뷰어 배율은 이산적이라, 폭만으로는 맞출 수 없다.
 *
 * 해법 — 100% 로 렌더한 뒤 컨테이너 폭에 맞게 **축소**한다.
 * 확대(transform: scale > 1)는 이미 렌더된 픽셀을 늘려 흐려지지만, 축소는 그렇지 않다.
 * 축소율은 컨테이너 폭에 비례하므로 JS 로 계산해 인라인 스타일로 넣는다(CSS 만으로는 부모 폭을 알 수 없다).
 *
 * SAFETY_MARGIN 은 계약서 종류별 용지 편차를 흡수하기 위한 여유다. 잘리는 것보다 여백이 낫다.
 */

/** iframe 에 항상 주는 폭. 이 값 이상이어야 뷰어가 100% 배율을 고른다(실측: 850px 은 부족). */
const IFRAME_WIDTH = 1100;

/** IFRAME_WIDTH 로 로드했을 때 문서가 차지하는 폭의 비율(실측 702 / 1100). */
const DOC_RATIO = 0.638;

/** 용지 편차 흡수용 여유(1%). */
const SAFETY_MARGIN = 0.99;

/**
 * 미리보기 iframe 을 컨테이너 폭에 맞춘다.
 * 컴포넌트 템플릿이 다중 루트(fragment)라 $el 로는 찾을 수 없으므로 ref 로 받은 엘리먼트를 직접 넘긴다.
 * @param {HTMLIFrameElement|null} iframe 미리보기 iframe 엘리먼트
 */
export function fitPrixPreview(iframe) {
    if (!iframe || !iframe.style) return;

    const wrap = iframe.parentElement;
    if (!wrap) return;

    const containerWidth = wrap.clientWidth;
    // 미리보기 컬럼이 아직 그려지기 전이거나 데스크톱 전용 컬럼이 숨겨진 상태면 폭이 0 이다.
    // 이때는 아무것도 하지 않고, 이후 resize 시점에 다시 계산된다.
    if (!containerWidth) return;

    const documentWidth = IFRAME_WIDTH * DOC_RATIO;
    const scale = Math.min(1, (containerWidth * SAFETY_MARGIN) / documentWidth);

    iframe.style.transformOrigin = 'top left';
    iframe.style.transform = `scale(${scale})`;
    // 축소된 iframe 을 컨테이너 가운데 두어, 문서(=iframe 가운데)가 가운데 오게 한다.
    iframe.style.marginLeft = `${(containerWidth - IFRAME_WIDTH * scale) / 2}px`;
}

/**
 * fitPrixPreview 를 즉시 한 번 실행하고 창 크기 변화에도 따라가게 한다.
 * @param {HTMLIFrameElement|null} iframe 미리보기 iframe 엘리먼트
 * @returns {() => void} 리스너 해제 함수
 */
export function attachPrixPreviewFit(iframe) {
    const run = () => fitPrixPreview(iframe);
    run();
    window.addEventListener('resize', run);
    return () => window.removeEventListener('resize', run);
}
